import type { ComponentProps } from 'react';

import type { MetricValues } from '@revelio/core';
import {
  BarChart,
  type BarData,
  getOtherColor,
  getPrimaryColor,
  NEGATIVE_COLOR,
  POSITIVE_COLOR,
  plotColors,
} from '@revelio/replots';

import { useCurrencyPreference } from '../../../../shared/user-preferences/hooks/useCurrencyPreference';
import type { SingleDataPoint, SingleDataSet } from '../DataProvider/types';
import { useChart } from './ChartProvider';
import { deriveSharedChartType } from './chart-menu/plot-share/deriveSharedChartType';
import { usePlotShareData } from './chart-menu/plot-share/usePlotShare';
import {
  isParameterSelectionEqualToDataPoint,
  type ParameterSelection,
} from './utils/check-datapoint-in-parameter';
import { getFormat } from './utils/getFormat';

type BarChartComponentProps = ComponentProps<typeof BarChart>;
const BENCHMARK_COLOR = '#93C5FD';

export const Bar = ({
  data,
  metric,
  maxValue,
  adjustedMin,
  sortByValue,
  coloredBars,
  color,
  primaryParameterValues,
  showDataLabels,
  comparisonTransformed,
}: {
  data: SingleDataSet['data'];
  metric?: MetricValues;
  maxValue?: number;
  adjustedMin?: number;
  sortByValue?: boolean;
  coloredBars?: boolean;
  color?: string;
  primaryParameterValues?: ParameterSelection[];
  showDataLabels?: boolean;
  comparisonTransformed?: boolean;
}) => {
  const { item } = useChart();
  const { code } = useCurrencyPreference();

  const hasSeriesTypePeerComparison = data.some(
    (p) => p.seriesType !== undefined
  );

  const getBarColor = ({
    point,
    index,
  }: {
    index: number;
    point: SingleDataPoint;
  }) => {
    const value = data[index]?.value ?? 0;

    if (comparisonTransformed) {
      return value < 0 ? NEGATIVE_COLOR : POSITIVE_COLOR;
    }

    if (coloredBars) {
      return plotColors[index % plotColors.length];
    }

    if (point.seriesType !== undefined) {
      if (point.seriesType === 'primary') return getPrimaryColor();
      if (point.seriesType === 'benchmark') return BENCHMARK_COLOR;
      return getOtherColor();
    }

    if (primaryParameterValues?.length) {
      const pointIsPrimary = primaryParameterValues.some((p) =>
        isParameterSelectionEqualToDataPoint(p, point)
      );
      return pointIsPrimary ? getPrimaryColor() : getOtherColor();
    }

    return getPrimaryColor();
  };

  const barChartData: BarData[] = data.map((point, index) => {
    const derivedColor = getBarColor({ index, point });
    const fillColor = hasSeriesTypePeerComparison
      ? derivedColor
      : (color ?? derivedColor);
    return {
      ...point,
      id: point.id ?? null,
      label: point.dim,
      value: point.value,
      color: fillColor,
    };
  });

  const format = item?.plot?.format ?? getFormat({ metric });

  const sortedBarChartData = sortByValue
    ? barChartData.sort((a, b) => {
        const va = a.value ?? 0;
        const vb = b.value ?? 0;
        if (vb !== va) return vb - va;
        return (a.label ?? '').localeCompare(b.label ?? '');
      })
    : barChartData;

  const lowestValue = sortedBarChartData.reduce((min, current) => {
    return Math.min(min, current.value ?? 0);
  }, 0);

  const minValue = Math.max(lowestValue - (adjustedMin ?? 0), 0);

  const chartProps: BarChartComponentProps = {
    data: sortedBarChartData,
    format,
    currencyCode: code,
    showBarLabels: showDataLabels,
    xAxis: maxValue ? { min: minValue ?? 0, max: maxValue } : undefined,
  };

  usePlotShareData({
    data: sortedBarChartData,
    chartType: deriveSharedChartType(BarChart),
    chartProps,
  });

  return <BarChart {...chartProps} />;
};
